import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft, Sparkles, Check, Palette, Heart, Target, Shirt } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import auraLogo from '@/assets/aura-logo.png';

interface StyleQuizProps {
  isOpen: boolean;
  onComplete: (preferences: StylePreferences) => void;
  onClose: () => void;
}

export interface StylePreferences {
  stylePersonality: string;
  preferredColors: string[];
  fashionGoals: string[];
  occasions: string[];
  bodyComfort: string;
  budgetRange: string;
}

const quizSteps = [
  {
    id: 'personality',
    title: 'Style Personality',
    subtitle: 'How would you describe your fashion identity?',
    icon: Sparkles,
    type: 'single',
    options: [
      { id: 'minimalist', label: 'Minimalist', desc: 'Clean lines, neutral tones, less is more', image: '🤍' },
      { id: 'classic', label: 'Classic', desc: 'Timeless pieces, elegant silhouettes', image: '🖤' },
      { id: 'trendy', label: 'Trendy', desc: 'Current fashion, experimental styles', image: '💜' },
      { id: 'bohemian', label: 'Bohemian', desc: 'Free-spirited, artistic, layered looks', image: '🧡' },
      { id: 'streetwear', label: 'Streetwear', desc: 'Urban, casual, statement pieces', image: '💙' },
      { id: 'glamorous', label: 'Glamorous', desc: 'Bold, luxurious, attention-grabbing', image: '💛' },
    ],
  },
  {
    id: 'colors',
    title: 'Color Palette',
    subtitle: 'Select colors that resonate with your wardrobe',
    icon: Palette,
    type: 'multi',
    maxSelect: 5,
    options: [
      { id: 'black', label: 'Black', color: '#0a0a0a' },
      { id: 'white', label: 'White', color: '#fafafa' },
      { id: 'navy', label: 'Navy', color: '#1e3a5f' },
      { id: 'beige', label: 'Beige', color: '#d4c4a8' },
      { id: 'burgundy', label: 'Burgundy', color: '#722f37' },
      { id: 'olive', label: 'Olive', color: '#556b2f' },
      { id: 'blush', label: 'Blush', color: '#de9e9e' },
      { id: 'camel', label: 'Camel', color: '#c19a6b' },
      { id: 'gray', label: 'Gray', color: '#808080' },
      { id: 'terracotta', label: 'Terracotta', color: '#cc7a6f' },
      { id: 'forest', label: 'Forest', color: '#228b22' },
      { id: 'lavender', label: 'Lavender', color: '#b57edc' },
    ],
  },
  {
    id: 'goals',
    title: 'Fashion Goals',
    subtitle: 'What do you want to achieve with AURA?',
    icon: Target,
    type: 'multi',
    maxSelect: 3,
    options: [
      { id: 'organize', label: 'Organize My Wardrobe', desc: 'Know exactly what I own' },
      { id: 'discover', label: 'Discover New Combos', desc: 'Find fresh outfit ideas' },
      { id: 'reduce', label: 'Shop Smarter', desc: 'Buy only what I need' },
      { id: 'confidence', label: 'Build Confidence', desc: 'Feel great in every outfit' },
      { id: 'sustainable', label: 'Dress Sustainably', desc: 'Maximize what I have' },
      { id: 'events', label: 'Event Ready', desc: 'Perfect looks for occasions' },
    ],
  },
  {
    id: 'occasions',
    title: 'Your Lifestyle',
    subtitle: 'Where do you need outfit inspiration most?',
    icon: Shirt,
    type: 'multi',
    maxSelect: 4,
    options: [
      { id: 'work', label: 'Work/Office', desc: 'Professional attire' },
      { id: 'casual', label: 'Casual Days', desc: 'Everyday comfort' },
      { id: 'datenight', label: 'Date Night', desc: 'Romantic occasions' },
      { id: 'events', label: 'Special Events', desc: 'Weddings, parties' },
      { id: 'active', label: 'Active/Athleisure', desc: 'Fitness & sporty' },
      { id: 'travel', label: 'Travel', desc: 'Versatile vacation looks' },
    ],
  },
  {
    id: 'comfort',
    title: 'Comfort Zone',
    subtitle: 'How adventurous are you with fashion?',
    icon: Heart,
    type: 'single',
    options: [
      { id: 'safe', label: 'Play It Safe', desc: 'Stick to what I know works' },
      { id: 'balanced', label: 'Balanced', desc: 'Mix classics with occasional risks' },
      { id: 'adventurous', label: 'Adventurous', desc: 'Love trying new styles' },
      { id: 'bold', label: 'Fearless', desc: 'Fashion is my self-expression' },
    ],
  },
];

export const StyleQuiz: React.FC<StyleQuizProps> = ({ isOpen, onComplete, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const step = quizSteps[currentStep];
  const progress = ((currentStep + 1) / quizSteps.length) * 100;

  const handleSelect = (optionId: string) => {
    if (step.type === 'single') {
      setAnswers({ ...answers, [step.id]: optionId });
    } else {
      const current = (answers[step.id] as string[]) || [];
      if (current.includes(optionId)) {
        setAnswers({ ...answers, [step.id]: current.filter(id => id !== optionId) });
      } else if (current.length < (step.maxSelect || 3)) {
        setAnswers({ ...answers, [step.id]: [...current, optionId] });
      }
    }
  };

  const isSelected = (optionId: string) => {
    const answer = answers[step.id];
    if (step.type === 'single') return answer === optionId;
    return Array.isArray(answer) && answer.includes(optionId);
  };

  const canProceed = () => {
    const answer = answers[step.id];
    if (step.type === 'single') return !!answer;
    return Array.isArray(answer) && answer.length > 0;
  };

  const handleNext = () => {
    if (currentStep < quizSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    
    const preferences: StylePreferences = {
      stylePersonality: answers.personality as string,
      preferredColors: answers.colors as string[] || [],
      fashionGoals: answers.goals as string[] || [],
      occasions: answers.occasions as string[] || [],
      bodyComfort: answers.comfort as string,
      budgetRange: 'medium',
    };

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error } = await supabase
          .from('profiles')
          .update({
            style_preferences: JSON.parse(JSON.stringify(preferences)),
            quiz_completed: true,
            style_personality: preferences.stylePersonality,
            preferred_colors: preferences.preferredColors,
            fashion_goals: preferences.fashionGoals,
          })
          .eq('id', user.id);

        if (error) throw error;
      }
      
      toast.success('Style profile created!', {
        description: 'AURA now understands your unique taste.',
      });
      onComplete(preferences);
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 bg-background border-border overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header with Logo */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={auraLogo} alt="AURA" className="w-10 h-10 object-contain" />
            <div>
              <h2 className="text-lg font-display tracking-wider text-foreground">Style Discovery</h2>
              <p className="text-xs text-muted-foreground">Step {currentStep + 1} of {quizSteps.length}</p>
            </div>
          </div>
          <div className="w-24 h-1 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-aura-gold to-aura-rose transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Step Header */}
              <div className="text-center mb-5 sm:mb-8">
                <div className="w-11 h-11 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 rounded-full bg-gradient-to-br from-aura-gold/20 to-aura-rose/20 flex items-center justify-center">
                  <step.icon size={20} className="text-foreground sm:w-6 sm:h-6" />
                </div>
                <h3 className="text-xl sm:text-2xl font-serif text-foreground mb-1.5 sm:mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-xs sm:text-sm">{step.subtitle}</p>
                {step.type === 'multi' && (
                  <p className="text-[10px] sm:text-xs text-accent mt-1.5 sm:mt-2">Select up to {step.maxSelect}</p>
                )}
              </div>

              {/* Options Grid */}
              <div className={`grid gap-2 sm:gap-3 ${step.id === 'colors' ? 'grid-cols-4 sm:grid-cols-6' : 'grid-cols-2'}`}>

                {step.options.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleSelect(option.id)}
                    className={`group relative p-2.5 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all duration-300 text-left ${
                      isSelected(option.id)
                        ? 'border-accent bg-accent/10 shadow-lg shadow-accent/20'
                        : 'border-border hover:border-muted-foreground/50 bg-card'
                    }`}
                  >
                    {step.id === 'colors' ? (
                      <div className="flex flex-col items-center gap-1.5 sm:gap-2">
                        <div 
                          className="w-7 h-7 sm:w-10 sm:h-10 rounded-full border-2 border-border shadow-inner"
                          style={{ backgroundColor: (option as any).color }}
                        />
                        <span className="text-[9px] sm:text-xs text-foreground">{option.label}</span>
                        {isSelected(option.id) && (
                          <Check size={12} className="absolute top-1.5 right-1.5 text-accent sm:w-[14px] sm:h-[14px] sm:top-2 sm:right-2" />
                        )}
                      </div>
                    ) : (
                      <>
                        {'image' in option && (
                          <span className="text-xl sm:text-2xl mb-1.5 sm:mb-2 block">{option.image}</span>
                        )}
                        <h4 className="font-medium text-foreground text-xs sm:text-sm">{option.label}</h4>
                        {'desc' in option && (
                          <p className="text-[9px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 line-clamp-2">{option.desc}</p>
                        )}
                        {isSelected(option.id) && (
                          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-accent flex items-center justify-center">
                            <Check size={10} className="text-accent-foreground sm:w-3 sm:h-3" />
                          </div>
                        )}
                      </>
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-border flex items-center justify-between gap-2 bg-background shrink-0">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30"
          >
            <ArrowLeft size={14} className="sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <button
            onClick={onClose}
            className="text-[10px] sm:text-xs text-muted-foreground hover:text-foreground"
          >
            Skip
          </button>
          <Button
            onClick={handleNext}
            disabled={!canProceed() || isSubmitting}
            className="bg-foreground text-background hover:bg-accent hover:text-accent-foreground px-4 sm:px-6 text-xs sm:text-sm h-9 sm:h-10 min-w-[100px]"
          >
            {currentStep === quizSteps.length - 1 ? (
              isSubmitting ? 'Saving...' : 'Complete'
            ) : (
              <>
                Continue
                <ArrowRight size={14} className="ml-1.5 sm:ml-2 sm:w-4 sm:h-4" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
